import { defineMeta } from '@/registry/types';

export const filterMenuMeta = defineMeta({
  name: 'FilterMenu',
  level: 'molecule',
  category: 'inputs',
  classification: 'custom',
  description:
    'One filter field behind a chip. The chip names the field and its current selection; the panel is a checkbox list with optional search, select-all and grouping. Selections apply live by default, or behind an Apply button.',
  baseLibrary: '@mui/material',
  muiEquivalent: 'Popover + Checkbox (composed)',
  muiDocs: 'https://mui.com/material-ui/react-popover/',
  importPath: '@/molecules/FilterMenu',
  requiredProps: ['field', 'options', 'onChange'],
  optionalProps: [
    'value',
    'defaultValue',
    'commit',
    'searchable',
    'showSelectAll',
    'summary',
    'onRemove',
    'onOpen',
    'onClose',
    'loading',
    'icon',
    'emptyText',
    'size',
  ],
  supportedStates: ['closed', 'open', 'empty', 'one selected', 'many selected', 'no matches', 'loading'],
  designTokens: ['colors', 'spacing', 'typography'],
  accessibility: [
    'The chip exposes aria-haspopup="dialog" and aria-expanded, and its accessible name spells out the whole selection, because the visible text elides the rest behind the +N counter.',
    'The panel is a named dialog, so a user who opened one of several chips in a filter bar knows which one they are in.',
    'Options are real checkboxes rather than menu rows, so selected state is announced natively and the panel can legally hold the search field and the Apply button.',
    'Escape closes the panel and returns focus to the chip; with `searchable`, focus lands in the filter field on open.',
  ],
  usageExamples: [
    '<FilterMenu field="Severity" options={severityOptions} value={severities} onChange={setSeverities} onRemove={() => dropFilter("severity")} />',
    '<FilterMenu field="Datasources" options={sources} value={picked} onChange={setPicked} summary="count" searchable showSelectAll />',
    '<FilterMenu field="Severity" options={severityOptions} value={severities} onChange={setSeverities} commit="apply" />',
    '<FilterMenu field="Severity" options={severityOptions} value={severities} onChange={setSeverities} onClose={(applied) => refetch(applied)} />',
  ],
  rules: [
    'Reach for this before a bare Chip with purpose="picker" — it owns the chip, the panel and the open state, and a picker chip on its own opens nothing.',
    'Keep `commit="immediate"` (the default) unless each change costs a query the user should not pay for mid-thought, or a partial selection would show a misleading result. Then use `commit="apply"`.',
    'Use `summary="count"` when option labels are too long to sit in a chip, such as datastore index names; the default names the first selection and counts the rest.',
    'Pass `searchable` past about ten options, and `showSelectAll` when picking most of them is a normal thing to do.',
    'Group options with `option.group` when the same name can come from more than one source, so the origin of a value is visible.',
    'When those sources can be enabled independently, pair the menu with a `Chip purpose="select"` per source and pass the filtered `options`. Each chip is a standalone on/off; turning one off removes that source\'s values from the panel.',
    'Pass `onRemove` to take the whole field out of the bar, which is what the X on the chip means — the same thing it means on `Chip purpose="filter"`. Pair it with an `Add filter` menu, and omit it for a bar of fixed fields, where the panel `Reset` is how a user empties the selection.',
    'Use `onClose` to run one query per visit instead of one per checkbox: keep `commit="immediate"` so the panel and chip stay live, track the selection from `onChange`, and refetch on close. Prefer this over `commit="apply"` when the only problem is query cost rather than a misleading partial result.',
    'Order `options` the way you want them read: the chip names the first selected option, and selections are held in option order rather than click order.',
    'Pass `size` to size the picker trigger only. Panel search, checkboxes and Apply/Reset stay `small`.',
  ],
  antiPatterns: [
    'Do not use this to change a record — it picks which values are filtered on, not the state of the thing underneath. Changing an alert status is a Menu behind a Button.',
    'Do not put a second FilterMenu inside the panel; nested popovers cannot be dismissed predictably.',
    'Do not pass raw option values as labels. The chip shows what you give it, so `sev_1` reaches the user.',
    'Do not use it for a single yes/no option; that is one Chip with purpose="select".',
    'Do not use it for many fields at once — that is FilterPopover, which commits them together.',
  ],
  mountsSurface: 'portal',
  llmSafe: true,
  whenToUse: [
    'A filter bar above a table or list, where each field is one chip',
    'Picking several values from a closed set, such as severities or data sources',
    'Anywhere the applied filter and the control that changes it should be the same element',
  ],
  whenNotToUse: [
    'Several filter fields committed together (use FilterPopover)',
    'An always-visible filter sidebar (use FilterPanel)',
    'A free-text or open-ended value (use Autocomplete)',
    'Displaying an applied criterion the user can only remove (use Chip purpose="filter")',
  ],
});

export default filterMenuMeta;
